import Profile from '../components/Profile'
import SwipeBackPage from '../components/SwipeBackPage'
import { getOtherPlayerProfile } from '../mockOtherProfiles'
import { getUpcomingGames } from '../teamForm'
import { canRevealOtherPlayerGameweek } from '../predictionCycle'
import { getSortedFinalizedGameweeks } from '../season'
import {
  buildBestGameweekRecord,
  buildChipTiles,
} from '../components/profile/records'
import { useOtherPlayerGameweekDetail } from './useOtherPlayerGameweekDetail'
import { buildOtherPlayerFixtureStates } from './otherPlayerFixtureStates'
import GameweekResultsModal from './GameweekResultsModal'
import type { AppView } from './AppView'
import type { ViewedPlayer } from './useViewedPlayer'

/**
 * Another player's profile, read-only.
 *
 * Their identity comes from the live server fetch, and from nowhere else: until it lands (or if
 * it fails) the header renders crest-less rather than guessing a club.
 * Their "GW N Calls" are only assembled while a gameweek is inside the reveal
 * window — between its deadline passing and it being finalized.
 */
export default function OtherPlayerScreen({
  view,
  player,
}: {
  view: AppView
  player: ViewedPlayer
}) {
  const {
    gameweek,
    identity,
    quests,
    rivals,
    scores,
    simulation,
    viewedPlayer,
  } = view
  const { currentGameweek, games, actualResults } = gameweek

  const bundle = getOtherPlayerProfile(
    player.userId,
    player.displayName,
    viewedPlayer.liveProfile ?? undefined
  )

  const revealGameweek = view.otherPlayerRevealGameweek
  let otherPlayerGameweekReveal = null
  if (revealGameweek !== null) {
    // The reveal window is only ever the current, not-yet-finalized gameweek, so its fixtures and
    // results are always the current ones.
    const fixtureStates = buildOtherPlayerFixtureStates({
      games,
      revealedResults: scores.revealedResults,
      simGameStatuses: simulation.gameStatuses,
      actualResults,
      liveResults: simulation.liveResults,
    })

    const picks = viewedPlayer.revealedPicks
    otherPlayerGameweekReveal = {
      gameweek: revealGameweek,
      games,
      predictions: picks.predictions,
      bankerGameId: picks.bankerGameId,
      activeChipId: picks.activeChipId,
      fixtureStates,
      questPicks: picks.questPicks,
      // App-wide truth, so the same map the viewer's own reveal reads serves this one.
      questResults: quests.questResults,
      questsEnabled: identity.questsEnabled,
    }
  }

  // Their best gameweek's marks need their picks for that week, so this is its own lazy fetch —
  // keyed on (userId, gameweek) so a response for a previously-viewed player is never painted.
  const best = bundle.seasonStats.bestGameweek
  const bestDetail = useOtherPlayerGameweekDetail(player.userId, best?.gameweek ?? null)

  // What the sharpest-week row's arrows step through: *their* finalized season, so the modal
  // walks their weeks and refetches each one's picks. A mock roster player with no live profile
  // has none, and the row stays the single-week view it is today.
  const finalizedGameweeks = getSortedFinalizedGameweeks(
    viewedPlayer.liveProfile?.seasonHistory ?? []
  )

  // Leaving the profile takes its results modal with it. Without this the modal's state would
  // outlive the screen that owns it and reappear over the tab shell, showing a stranger's
  // gameweek on your own home tab.
  const closeProfile = () => {
    view.history.close()
    viewedPlayer.clearViewedPlayer()
  }

  const rivalStatus: 'none' | 'pending' | 'rival' = rivals.rivals.some(
    (r) => r.userId === player.userId
  )
    ? 'rival'
    : rivals.outgoingRequests.some((r) => r.toUserId === player.userId)
      ? 'pending'
      : 'none'

  return (
    <>
      <SwipeBackPage onBack={closeProfile}>
        <Profile
          profile={bundle.profile}
          leagues={[]}
          recentResults={bundle.recentResults}
          upcomingFixtures={getUpcomingGames(
            bundle.profile.favouriteTeam,
            currentGameweek
          )}
          overallScore={bundle.overallScore}
          seasonStats={bundle.seasonStats}
          formHistory={bundle.formHistory}
          bestGameweek={buildBestGameweekRecord(
            bundle.seasonStats.bestGameweek,
            bestDetail
          )}
          chipTiles={buildChipTiles({
            // Theirs or nothing — never the viewer's `useChips` ledger under someone else's name.
            ledger: bundle.chipsUsed,
            seasonHistory: viewedPlayer.liveProfile?.seasonHistory ?? [],
            settledGameweeks: gameweek.settledGameweeks,
            currentGameweek,
            // A chip played for the round in play is a pre-deadline choice, and the app already
            // refuses to leak those — same gate as the "GW N Calls" reveal.
            // It stays open until the app advances, so the settled evening before rollover still
            // shows the chip they burned rather than an empty "0 of 2".
            canRevealCurrentGameweek: canRevealOtherPlayerGameweek(
              currentGameweek,
              gameweek.deadlinePassedGameweeks.includes(currentGameweek)
            ),
          })}
          sharpestWeekWalk={finalizedGameweeks}
          onOpenGameweek={(gw, walk) => {
            const owner = {
              userId: player.userId,
              displayName: bundle.profile.name,
              bestGameweek: bundle.seasonStats.bestGameweek?.gameweek ?? null,
            }
            if (walk) view.history.selectWalk(gw, walk, owner)
            else view.history.selectForPlayer(gw, owner)
          }}
          currentGameweek={currentGameweek}
          avatarPreset={bundle.profile.avatarPreset}
          viewMode="other"
          otherPlayerGameweekReveal={otherPlayerGameweekReveal}
          onBack={closeProfile}
          rivalStatus={rivalStatus}
          onRivalAction={() =>
            rivalStatus === 'rival'
              ? rivals.endRivalry(player.userId, player.displayName)
              : rivalStatus === 'none'
                ? rivals.challenge(player.userId, player.displayName)
                : undefined
          }
        />
      </SwipeBackPage>
      {/* Outside the swipe-back page: the results modal is an overlay above this screen, and the
          tab shell's own `AppModals` never mounts here. */}
      <GameweekResultsModal view={view} />
    </>
  )
}
